/**
 * Edit History Plugin for ProseMirror
 *
 * This plugin tracks edit events for AI detection and academic integrity
 * monitoring. It records detailed information about how a document was
 * written, including:
 *
 * - Character insertions and deletions
 * - Paste events (distinguished from typing)
 * - Undo/redo operations
 * - Keystroke timing intervals
 *
 * The collected data can be analyzed to detect patterns that suggest
 * AI-generated content (e.g., uniform typing speed, large paste events).
 */

import type { Transaction, EditorState } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode, Schema } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ReplaceStep, ReplaceAroundStep, Step } from "@tiptap/pm/transform";
import { isHistoryTransaction } from "@tiptap/pm/history";
import { blockKeyPluginKey } from "./blockKeyPlugin";

/**
 * Meta key used to mark transactions as sync operations.
 *
 * Sync operations are programmatic content updates that should not be
 * recorded as user edit events. Examples:
 * - Initial content load when editor mounts
 * - Restoring content from server or local backup
 * - Content sync after save
 *
 * To mark a transaction as sync:
 * ```typescript
 * const tr = view.state.tr.replaceWith(...).setMeta(SYNC_TRANSACTION_META, true);
 * view.dispatch(tr);
 * ```
 */
export const SYNC_TRANSACTION_META = "editHistory:sync";

/**
 * Convert a ProseMirror position to a text offset.
 * ProseMirror positions include node boundaries, but we need plain text offsets
 * for the replay feature to work correctly.
 *
 * @param doc - The ProseMirror document
 * @param pmPosition - The ProseMirror position
 * @returns The equivalent text offset (1-indexed to match PM convention)
 */
function pmPositionToTextOffset(doc: ProseMirrorNode, pmPosition: number): number {
  try {
    // Get the text content from the start of the document to the position
    // This gives us the text offset equivalent
    const textBeforePos = doc.textBetween(0, pmPosition, "\n");
    // Return 1-indexed position (add 1 because text offset 0 = position 1)
    return textBeforePos.length + 1;
  } catch {
    // If position is invalid, return the original (clamped to valid range)
    return Math.max(1, pmPosition);
  }
}

/**
 * A fixed-capacity circular buffer that overwrites oldest elements when full.
 * Provides O(1) push and O(n) toArray operations.
 *
 * Used to efficiently store edit history events without array reallocation
 * on every keystroke.
 */
export class CircularBuffer<T> {
  private buffer: (T | undefined)[];
  private head: number = 0; // Next write position
  private _size: number = 0;
  readonly capacity: number;

  constructor(capacity: number) {
    if (capacity < 1) {
      throw new Error("CircularBuffer capacity must be at least 1");
    }
    this.capacity = capacity;
    this.buffer = new Array<T | undefined>(capacity).fill(undefined);
  }

  get length(): number {
    return this._size;
  }

  /**
   * Add a single item to the buffer. O(1) operation.
   * If buffer is full, overwrites the oldest item.
   */
  push(item: T): void {
    this.buffer[this.head] = item;
    this.head = (this.head + 1) % this.capacity;
    if (this._size < this.capacity) {
      this._size++;
    }
  }

  /**
   * Add multiple items to the buffer. O(m) where m is items.length.
   * Optimized for the case where items.length >= capacity.
   */
  pushMany(items: readonly T[]): void {
    if (items.length === 0) return;

    if (items.length >= this.capacity) {
      // Only keep the last 'capacity' items - no point iterating over ones
      // that will be immediately overwritten
      const startIdx = items.length - this.capacity;
      for (let i = 0; i < this.capacity; i++) {
        this.buffer[i] = items[startIdx + i];
      }
      this.head = 0;
      this._size = this.capacity;
    } else {
      for (const item of items) {
        this.push(item);
      }
    }
  }

  /**
   * Convert buffer to array in insertion order (oldest to newest).
   * O(n) operation where n is buffer length.
   */
  toArray(): T[] {
    if (this._size === 0) return [];

    const result: T[] = [];
    // Calculate tail position (oldest item)
    const tail = (this.head - this._size + this.capacity) % this.capacity;

    for (let i = 0; i < this._size; i++) {
      result.push(this.buffer[(tail + i) % this.capacity] as T);
    }

    return result;
  }

  /**
   * Clear all items from the buffer. O(1) operation.
   */
  clear(): void {
    this.head = 0;
    this._size = 0;
    // Clear references to allow garbage collection
    this.buffer = new Array<T | undefined>(this.capacity).fill(undefined);
  }
}

/**
 * Registry for edit history buffers, keyed by session ID.
 * Allows buffers to survive plugin recreation (e.g., hot module reload).
 */
const editHistoryBuffers = new Map<string, CircularBuffer<EditEvent>>();

/**
 * A captured ProseMirror step with its timestamp.
 * Used for rich text replay where we need the native Steps, not just text events.
 */
export interface CapturedStep {
  /** The step serialized via Step.toJSON() */
  stepJson: Record<string, unknown>;
  /** When this step occurred */
  timestamp: number;
  /** Index of this step within the transaction (0-based) */
  stepIndex: number;
  /**
   * Monotonic sequence number for this step within the session.
   * Used for unique key generation when timestamps collide.
   * Increments for each captured step, ensuring no duplicates.
   */
  sequenceNumber: number;
}

/**
 * Registry for ProseMirror step buffers, keyed by session ID.
 * Separate from edit events to keep text replay and rich replay independent.
 */
const pmStepsBuffers = new Map<string, CircularBuffer<CapturedStep>>();

/**
 * Monotonic sequence counter for steps within a session.
 * Ensures unique identification even when timestamps collide.
 */
const stepSequenceCounters = new Map<string, number>();

/**
 * Get next sequence number for a session.
 */
function getNextSequenceNumber(sessionId: string): number {
  const current = stepSequenceCounters.get(sessionId) ?? 0;
  const next = current + 1;
  stepSequenceCounters.set(sessionId, next);
  return next;
}

/**
 * Get or create a circular buffer for a session.
 * If an existing buffer has a different capacity, it is replaced.
 */
function getOrCreateBuffer(
  sessionId: string,
  capacity: number
): CircularBuffer<EditEvent> {
  let buffer = editHistoryBuffers.get(sessionId);
  if (!buffer || buffer.capacity !== capacity) {
    buffer = new CircularBuffer<EditEvent>(capacity);
    editHistoryBuffers.set(sessionId, buffer);
  }
  return buffer;
}

/**
 * Get or create a ProseMirror steps buffer for a session.
 * If an existing buffer has a different capacity, it is replaced.
 */
function getOrCreateStepsBuffer(
  sessionId: string,
  capacity: number
): CircularBuffer<CapturedStep> {
  let buffer = pmStepsBuffers.get(sessionId);
  if (!buffer || buffer.capacity !== capacity) {
    buffer = new CircularBuffer<CapturedStep>(capacity);
    pmStepsBuffers.set(sessionId, buffer);
  }
  return buffer;
}

/**
 * Clean up buffers for a session.
 * Call this when the editor is destroyed to free memory.
 */
export function cleanupEditHistory(sessionId: string): void {
  editHistoryBuffers.delete(sessionId);
  pmStepsBuffers.delete(sessionId);
}

/**
 * Types of edit events that can occur.
 */
export type EditEventType =
  | "insert"
  | "delete"
  | "replace"
  | "paste"
  | "undo"
  | "redo";

/**
 * An edit event recorded by the plugin.
 */
export interface EditEvent {
  /** Unique identifier for this event */
  id: string;
  /** When this event occurred */
  timestamp: number;
  /** Type of edit operation */
  type: EditEventType;
  /** Document position where the edit occurred */
  position: number;
  /** Length of content affected */
  length: number;
  /** Content that was inserted (for insert/paste/replace) */
  content?: string;
  /** Content that was deleted (for delete/replace) */
  deletedContent?: string;
  /** Milliseconds since last keystroke (for detecting typing patterns) */
  keystrokeIntervalMs?: number;
  /** Session ID to group events from same editing session */
  sessionId: string;
  /**
   * Index of the step that created this event within the transaction.
   * Used for matching ProseMirror steps to events.
   * For coalesced events (paste), this is the index of the first step.
   */
  stepIndex?: number;
  /**
   * Sequence number(s) of the PM step(s) that created this event.
   * Used for unique matching when timestamps collide.
   * Single number for regular events, array for coalesced events (paste, formatting).
   */
  stepSequenceNumbers?: number[];
}

interface PluginState {
  /**
   * Version counter that increments when events are added.
   * Used to signal state changes without storing events in immutable plugin state.
   */
  version: number;
  lastEventTime: number;
  sessionId: string;
}

export const editHistoryPluginKey = new PluginKey<PluginState>("editHistory");

/**
 * Get all edit events from the editor state.
 * Events are stored in a circular buffer external to plugin state for O(1) push.
 */
export function getEditEvents(state: EditorState): EditEvent[] {
  const pluginState = editHistoryPluginKey.getState(state);
  if (!pluginState) return [];

  const buffer = editHistoryBuffers.get(pluginState.sessionId);
  return buffer?.toArray() ?? [];
}

/**
 * Get all captured ProseMirror steps from the editor state.
 * Steps are stored in a circular buffer external to plugin state for O(1) push.
 * Used for rich text replay with formatting preservation.
 */
export function getProseMirrorSteps(state: EditorState): CapturedStep[] {
  const pluginState = editHistoryPluginKey.getState(state);
  if (!pluginState) return [];

  const buffer = pmStepsBuffers.get(pluginState.sessionId);
  return buffer?.toArray() ?? [];
}

/**
 * Deserialize captured steps back to ProseMirror Step objects.
 * Use this to replay steps on a document.
 *
 * @param capturedSteps - Steps captured via getProseMirrorSteps
 * @param schema - The ProseMirror schema to deserialize with
 * @returns Array of deserialized Step objects with timestamps
 */
export function deserializeSteps(
  capturedSteps: CapturedStep[],
  schema: Schema
): { step: Step; timestamp: number }[] {
  const results: { step: Step; timestamp: number }[] = [];

  for (const captured of capturedSteps) {
    try {
      const step = Step.fromJSON(schema, captured.stepJson);
      results.push({ step, timestamp: captured.timestamp });
    } catch {
      // Skip steps that can't be deserialized (schema mismatch, corrupted data)
      console.warn("Failed to deserialize step:", captured.stepJson);
    }
  }

  return results;
}

/**
 * Generate a unique ID for events.
 */
function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Generate a session ID for grouping events.
 */
function generateSessionId(): string {
  return `session-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Detect if a transaction represents a paste operation.
 * Paste transactions have a characteristic pattern:
 * - First step inserts content
 * - Subsequent steps are ProseMirror normalization (merge nodes, cleanup)
 * - All happen atomically (same timestamp)
 */
function isPasteTransaction(tr: Transaction): boolean {
  if (tr.steps.length <= 1) {
    return false; // Single step, analyze normally
  }

  // Check first step: must be an insert with substantial content
  // Paste operations may use ReplaceStep OR ReplaceAroundStep
  const firstStep = tr.steps[0];
  const isReplaceType =
    firstStep instanceof ReplaceStep || firstStep instanceof ReplaceAroundStep;
  if (!isReplaceType) {
    return false;
  }

  // For paste detection, compare document before/after to find inserted content
  // This works for both ReplaceStep and ReplaceAroundStep
  const beforeText = tr.before.textContent;
  const afterText = tr.doc.textContent;

  // The inserted content length is the difference in document sizes
  const insertedLength = afterText.length - beforeText.length;
  if (insertedLength <= 0) {
    return false; // No content inserted
  }

  // Get the actual inserted text from the new document
  // firstStep is known to be ReplaceStep | ReplaceAroundStep at this point
  const pastePosition = firstStep.from;
  const insertedContent = afterText.substring(
    Math.max(0, pastePosition),
    Math.min(afterText.length, pastePosition + insertedLength)
  );

  // Must have significant content to be a paste
  if (insertedContent.length <= 1) {
    return false; // Single character inserts are typing, not paste
  }

  // Check if content suggests paste (length or newlines)
  const hasMultipleNewlines = (insertedContent.match(/\n/g) ?? []).length > 1;
  const isSubstantialContent = insertedContent.length > 20;

  if (!hasMultipleNewlines && !isSubstantialContent) {
    return false; // Small single-line insert, likely typing
  }

  // Check remaining steps: should be within the pasted content range
  // Normalization steps occur at paragraph boundaries throughout the pasted content
  const pasteStart = firstStep.from;
  const pasteEnd = firstStep.from + insertedLength;

  for (let i = 1; i < tr.steps.length; i++) {
    const step = tr.steps[i];
    if (step instanceof ReplaceStep || step instanceof ReplaceAroundStep) {
      const stepPosition = step.from;
      // Normalization steps should be within the pasted content range
      // Allow small buffer before paste start for edge cases
      if (stepPosition < pasteStart - 10 || stepPosition > pasteEnd + 10) {
        // Position outside pasted content range - not a paste normalization step
        return false;
      }
    } else {
      return false; // Non-replace step in multi-step transaction
    }
  }

  return true;
}

/**
 * Extract text content from a ProseMirror Fragment, preserving paragraph breaks.
 *
 * Uses Fragment.textBetween() which is the proper ProseMirror API for this.
 * See: https://discuss.prosemirror.net/t/node-textcontent-returns-text-without-whitespace-between-nodes/3109
 *
 * @param content - A ProseMirror Fragment (node.content or slice.content)
 * @param leafText - Text to use for leaf nodes like hardBreak (default: "\n")
 */
function extractTextFromFragment(
  content: ProseMirrorNode["content"],
  leafText: string = "\n"
): string {
  // Fragment.textBetween handles paragraph breaks and leaf nodes correctly
  return content.textBetween(0, content.size, "\n", leafText);
}

/**
 * Coalesce multiple transaction steps into a single paste event.
 * Extracts the inserted content from the first step and ignores normalization steps.
 * Converts ProseMirror positions to text offsets for replay compatibility.
 */
function coalesceTransactionSteps(
  tr: Transaction,
  oldDoc: ProseMirrorNode
): {
  position: number;
  content: string;
  length: number;
} {
  // First step can be ReplaceStep or ReplaceAroundStep
  const firstStep = tr.steps[0] as ReplaceStep | ReplaceAroundStep;
  // Convert ProseMirror position to text offset
  const position = pmPositionToTextOffset(oldDoc, firstStep.from);

  // Extract inserted content from first step, preserving paragraph and line breaks
  const slice = firstStep.slice;
  const content = slice ? extractTextFromFragment(slice.content) : "";

  return {
    position,
    content,
    length: content.length,
  };
}

export interface EditHistoryPluginOptions {
  /**
   * Called when an edit event is recorded.
   * Use this to stream events to the server or log them.
   */
  onEvent?: (event: EditEvent) => void;

  /**
   * Called when ProseMirror steps are captured.
   * Use this to stream steps to the server for rich text replay.
   */
  onSteps?: (steps: CapturedStep[]) => void;

  /**
   * Maximum number of events to keep in memory.
   * Older events are discarded when this limit is reached.
   * Defaults to 10000.
   */
  maxEvents?: number;

  /**
   * Session ID to use for all events.
   * If not provided, a new session ID is generated.
   */
  sessionId?: string;

  /**
   * Threshold in milliseconds to consider an insertion as a paste.
   * If characters are inserted faster than typing could produce them,
   * it's likely a paste. Defaults to 20ms per character.
   */
  pasteThresholdMsPerChar?: number;
}

/**
 * Creates the edit history tracking plugin.
 */
export function createEditHistoryPlugin(
  options: EditHistoryPluginOptions = {}
): Plugin<PluginState> {
  const {
    onEvent,
    onSteps,
    maxEvents = 10000,
    sessionId = generateSessionId(),
    pasteThresholdMsPerChar = 20,
  } = options;

  // Get or create the circular buffers for this session
  // These persist across plugin recreations (e.g., hot module reload)
  const buffer = getOrCreateBuffer(sessionId, maxEvents);
  const stepsBuffer = getOrCreateStepsBuffer(sessionId, maxEvents);

  return new Plugin<PluginState>({
    key: editHistoryPluginKey,

    state: {
      init(): PluginState {
        return {
          version: buffer.length,
          lastEventTime: Date.now(),
          sessionId,
        };
      },

      apply(tr, pluginState): PluginState {
        // Skip if no document changes
        if (!tr.docChanged) {
          return pluginState;
        }

        const now = Date.now();
        const keystrokeIntervalMs = now - pluginState.lastEventTime;

        // Skip sync transactions - these are programmatic content updates, not user edits
        if (tr.getMeta(SYNC_TRANSACTION_META) === true) {
          return {
            ...pluginState,
            lastEventTime: now,
          };
        }

        // Check if this transaction should create edit events
        // We need to check this BEFORE capturing steps, so we don't capture
        // orphan steps from transactions that won't create events (like blockKey changes)
        const shouldCreateEvent = shouldTransactionCreateEvent(tr);

        // Capture ProseMirror steps only for transactions that will create events
        // This prevents orphan steps from blockKeyPlugin (ReplaceAroundStep for attrs only)
        // from being captured and causing position mismatch during replay
        const capturedSteps = shouldCreateEvent
          ? captureTransactionSteps(tr, now, pluginState.sessionId)
          : [];
        if (capturedSteps.length > 0) {
          stepsBuffer.pushMany(capturedSteps);
          onSteps?.(capturedSteps);
        }

        // Analyze transaction for text-based edit events
        const newEvents = shouldCreateEvent
          ? analyzeTransaction(
              tr,
              pluginState.sessionId,
              now,
              keystrokeIntervalMs,
              pasteThresholdMsPerChar,
              capturedSteps
            )
          : [];

        // Notify callback for each event
        newEvents.forEach((event) => {
          onEvent?.(event);
        });

        // Add events to circular buffer - O(m) where m is newEvents.length
        // This replaces the previous O(n) spread + slice operation
        buffer.pushMany(newEvents);

        return {
          version: pluginState.version + newEvents.length,
          lastEventTime: now,
          sessionId: pluginState.sessionId,
        };
      },
    },
  });
}

/**
 * Check if a transaction should create edit events and have its steps captured.
 *
 * This is used to filter out internal transactions like blockKey assignment
 * that don't represent user edits. Capturing steps from these transactions
 * would cause position mismatches during replay because the steps exist
 * but aren't attached to any event.
 *
 * @param tr - The transaction to check
 * @returns true if this transaction should create edit events
 */
function shouldTransactionCreateEvent(tr: Transaction): boolean {
  // Skip sync transactions
  if (tr.getMeta(SYNC_TRANSACTION_META) === true) {
    return false;
  }

  if (tr.getMeta(blockKeyPluginKey) === true) {
    return false;
  }

  if (!tr.docChanged) {
    return false;
  }

  const oldDoc = tr.before;

  if (tr.getMeta("addToHistory") === false) {
    const step = tr.steps[0];
    const isLargeReplace =
      tr.steps.length === 1 &&
      step instanceof ReplaceStep &&
      step.from <= 2 &&
      step.to - step.from >= oldDoc.content.size * 0.5;

    if (isLargeReplace) {
      return false;
    }
  }

  return true;
}

/**
 * Capture all steps from a transaction as serialized JSON.
 * These steps can be deserialized and replayed for rich text replay.
 *
 * Each step is assigned a monotonically increasing sequence number within the
 * session to ensure unique identification even when multiple transactions
 * occur within the same millisecond (timestamp collision).
 */
function captureTransactionSteps(
  tr: Transaction,
  timestamp: number,
  sessionId: string
): CapturedStep[] {
  const capturedSteps: CapturedStep[] = [];

  for (let i = 0; i < tr.steps.length; i++) {
    const step = tr.steps[i];
    try {
      const stepJson = step.toJSON() as Record<string, unknown>;
      const sequenceNumber = getNextSequenceNumber(sessionId);
      capturedSteps.push({ stepJson, timestamp, stepIndex: i, sequenceNumber });
    } catch {
      // Skip steps that can't be serialized (should be rare)
      console.warn("Failed to serialize step:", step);
    }
  }

  return capturedSteps;
}

/**
 * Analyze a transaction and extract edit events.
 *
 * @param capturedSteps - The captured PM steps for this transaction, used to assign
 *   sequence numbers for unique matching when timestamps collide.
 */
function analyzeTransaction(
  tr: Transaction,
  sessionId: string,
  timestamp: number,
  keystrokeIntervalMs: number,
  pasteThresholdMsPerChar: number,
  capturedSteps: CapturedStep[]
): EditEvent[] {
  const events: EditEvent[] = [];
  const oldDoc = tr.before;

  // Check if text content actually changed
  // IMPORTANT: Use textBetween with "\n" separator to include paragraph breaks,
  // because textContent alone doesn't include separators between blocks.
  // See: https://discuss.prosemirror.net/t/node-textcontent-returns-text-without-whitespace-between-nodes/3109
  const oldText = oldDoc.textBetween(0, oldDoc.content.size, "\n");
  const newText = tr.doc.textBetween(0, tr.doc.content.size, "\n");
  const hasTextChange = oldText !== newText;

  // For formatting-only changes (heading, bold, etc.), text doesn't change but we still
  // need to create an event so PM steps get attached for rich text replay.
  // Check for steps that aren't ReplaceStep/ReplaceAroundStep (like AddMarkStep, RemoveMarkStep).
  // Note: We intentionally exclude ReplaceAroundStep from this check because:
  // - ReplaceAroundStep for node type changes (heading, list) comes WITH text changes
  // - ReplaceAroundStep for blockKey assignment is an internal detail that shouldn't create events
  const hasFormattingSteps = tr.steps.some(
    (step) => !(step instanceof ReplaceStep) && !(step instanceof ReplaceAroundStep)
  );
  const hasStructuralSteps = tr.steps.some(
    (step) => step instanceof ReplaceStep || step instanceof ReplaceAroundStep
  );

  // Skip if neither text changed nor formatting/structural changes occurred
  if (!hasTextChange && !hasFormattingSteps && !hasStructuralSteps) {
    return events;
  }

  // Skip sync transactions - these are programmatic content updates, not user edits
  // Check for explicit sync meta first (most reliable)
  if (tr.getMeta(SYNC_TRANSACTION_META) === true) {
    return events;
  }

  // Fallback: check addToHistory meta for transactions marked as non-undoable
  // This catches sync operations that set addToHistory: false but forgot sync meta
  // Only apply this fallback for large replacements to avoid false positives
  if (tr.getMeta("addToHistory") === false) {
    const step = tr.steps[0];
    const isLargeReplace =
      tr.steps.length === 1 &&
      step instanceof ReplaceStep &&
      step.from <= 2 &&
      step.to - step.from >= oldDoc.content.size * 0.5;

    if (isLargeReplace) {
      return events;
    }
  }

  // Check for undo/redo via the exported isHistoryTransaction function from prosemirror-history.
  // Note: tr.getMeta("history") as a string doesn't work because prosemirror-history
  // uses a PluginKey internally. However, the isHistoryTransaction function uses
  // the internal historyKey to correctly detect history transactions.
  if (isHistoryTransaction(tr)) {
    // To determine undo vs redo, check the meta value's 'redo' property.
    // We access the internal meta object since getMeta("history") doesn't work
    // with string keys when the meta was set with a PluginKey.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    const meta = (tr as any).meta as Record<string, unknown> | undefined;
    let isRedo = false;

    if (meta) {
      // The key in the meta object is the PluginKey's key property, which is "history"
      const historyMeta = meta["history"] as { redo?: boolean } | undefined;
      isRedo = historyMeta?.redo === true;
    }

    events.push({
      id: generateEventId(),
      timestamp,
      type: isRedo ? "redo" : "undo",
      position: 0,
      length: 0,
      sessionId,
    });
    return events;
  }

  // Check for paste transactions (multiple steps from single paste operation)
  // This must come BEFORE step-by-step analysis to avoid spurious replace events
  if (isPasteTransaction(tr)) {
    const { position, content, length } = coalesceTransactionSteps(tr, oldDoc);

    events.push({
      id: generateEventId(),
      timestamp,
      type: "paste",
      position,
      length,
      content,
      keystrokeIntervalMs,
      sessionId,
      stepIndex: 0, // Paste events coalesce all steps, match to first step
      // All sequence numbers from this transaction (paste coalesces all steps)
      stepSequenceNumbers: capturedSteps.map((s) => s.sequenceNumber),
    });

    return events; // Don't process individual steps
  }

  // Analyze each step individually (non-paste multi-step transactions)
  tr.steps.forEach((step, index) => {
    if (!(step instanceof ReplaceStep) && !(step instanceof ReplaceAroundStep)) {
      return;
    }

    // Get ProseMirror positions
    const pmFrom = (step as ReplaceStep).from ?? 0;
    const pmTo = (step as ReplaceStep).to ?? 0;
    // Convert to text offsets for replay compatibility
    const textPosition = pmPositionToTextOffset(oldDoc, pmFrom);

    // Calculate deleted content length (in PM positions, then we'll use actual text)
    const pmDeletedLength = pmTo - pmFrom;
    let deletedContent: string | undefined;
    if (pmDeletedLength > 0) {
      try {
        deletedContent = oldDoc.textBetween(pmFrom, pmTo, "\n");
      } catch {
        // Position might be invalid, skip content capture
      }
    }

    // Calculate inserted content, preserving paragraph and line breaks
    const slice = (step as ReplaceStep).slice;
    const insertedLength = slice?.content.size ?? 0;
    let insertedContent: string | undefined;
    if (insertedLength > 0) {
      insertedContent = extractTextFromFragment(slice.content);

      // When inserting an empty paragraph (pressing Enter), the content would be empty
      // but we still need to record this as a newline insertion
      // Count textblocks to detect this case
      let blockCount = 0;
      slice.content.forEach((node) => {
        if (node.isTextblock) blockCount++;
      });

      if (insertedContent === "" && blockCount > 0) {
        insertedContent = "\n";
      }
    }

    // Determine event type based on PM lengths (to know if insert/delete/replace)
    let eventType: EditEventType;
    if (pmDeletedLength > 0 && insertedLength > 0) {
      eventType = "replace";
    } else if (pmDeletedLength > 0) {
      eventType = "delete";
    } else if (insertedLength > 0) {
      // Check if this is likely a paste (large insertion, fast timing)
      const isPaste = isPasteEvent(
        insertedContent ?? "",
        keystrokeIntervalMs,
        pasteThresholdMsPerChar
      );
      eventType = isPaste ? "paste" : "insert";
    } else {
      // No content change, skip
      return;
    }

    // Use actual text lengths for event length, not ProseMirror positions
    // ProseMirror's slice.content.size includes node boundaries (2 per paragraph),
    // but we need plain text length for replay to work correctly
    const actualInsertedLength = insertedContent?.length ?? 0;
    const actualDeletedLength = deletedContent?.length ?? 0;

    // Find the captured step for this index to get its sequence number
    const capturedStep = capturedSteps.find((s) => s.stepIndex === index);
    events.push({
      id: generateEventId(),
      timestamp,
      type: eventType,
      position: textPosition,
      length: Math.max(actualDeletedLength, actualInsertedLength),
      content: insertedContent,
      deletedContent,
      keystrokeIntervalMs: index === 0 ? keystrokeIntervalMs : undefined,
      sessionId,
      stepIndex: index, // Track which step this event corresponds to
      stepSequenceNumbers: capturedStep ? [capturedStep.sequenceNumber] : undefined,
    });
  });

  // Handle formatting-only changes (heading, bold, etc.) that don't have ReplaceStep/ReplaceAroundStep.
  // These need an event so PM steps can be attached for rich text replay.
  // Create a single "replace" event with no content change to carry the formatting steps.
  if (events.length === 0 && tr.steps.length > 0) {
    // Find the position affected by the first step
    const firstStep = tr.steps[0];
    // Most formatting steps have 'from' property
    const from = "from" in firstStep ? (firstStep as { from: number }).from : 0;
    const textPosition = pmPositionToTextOffset(oldDoc, from);

    events.push({
      id: generateEventId(),
      timestamp,
      type: "replace", // Use "replace" as a generic formatting change type
      position: textPosition,
      length: 0, // No text content changed
      keystrokeIntervalMs,
      sessionId,
      stepIndex: 0, // Formatting events get all steps from this timestamp
      // All sequence numbers from this transaction (formatting coalesces all steps)
      stepSequenceNumbers: capturedSteps.map((s) => s.sequenceNumber),
    });
  }

  return events;
}

/**
 * Determine if an insertion is likely a paste event.
 *
 * Heuristics:
 * - Multiple characters inserted at once
 * - Contains newlines (multi-paragraph paste)
 * - Typing speed would be impossibly fast
 */
function isPasteEvent(
  content: string,
  keystrokeIntervalMs: number,
  thresholdMsPerChar: number
): boolean {
  // Single character is never considered a paste
  if (content.length <= 1) {
    return false;
  }

  // Multiple newlines strongly suggest paste
  if ((content.match(/\n/g) ?? []).length > 1) {
    return true;
  }

  // Check if typing speed is unrealistic
  // Average human typing is ~200ms between keystrokes
  // Anything faster than threshold per character is suspicious
  const expectedMinTime = content.length * thresholdMsPerChar;
  if (keystrokeIntervalMs < expectedMinTime && content.length > 3) {
    return true;
  }

  // Long content inserted at once
  if (content.length > 20) {
    return true;
  }

  return false;
}

/**
 * Calculate metrics from a collection of edit events.
 * Useful for AI detection analysis.
 */
export function calculateEditMetrics(events: EditEvent[]): EditMetrics {
  if (events.length === 0) {
    return {
      totalEvents: 0,
      totalKeystrokes: 0,
      totalPastes: 0,
      totalUndos: 0,
      totalRedos: 0,
      averageKeystrokeIntervalMs: 0,
      keystrokeIntervalVariance: 0,
      largestPasteSize: 0,
      sessionDurationMs: 0,
      wordsPerMinute: 0,
      pasteRatio: 0,
    };
  }

  // Count event types
  const inserts = events.filter((e) => e.type === "insert");
  const pastes = events.filter((e) => e.type === "paste");
  const undos = events.filter((e) => e.type === "undo");
  const redos = events.filter((e) => e.type === "redo");

  // Calculate keystroke intervals
  const intervals = events
    .map((e) => e.keystrokeIntervalMs)
    .filter((i): i is number => i !== undefined && i > 0 && i < 10000);

  const avgInterval =
    intervals.length > 0
      ? intervals.reduce((a, b) => a + b, 0) / intervals.length
      : 0;

  const variance =
    intervals.length > 0
      ? intervals.reduce((sum, i) => sum + Math.pow(i - avgInterval, 2), 0) /
        intervals.length
      : 0;

  // Find largest paste
  const pastesSizes = pastes.map((e) => e.content?.length ?? 0);
  const largestPaste = pastesSizes.length > 0 ? Math.max(...pastesSizes) : 0;

  // Calculate session duration
  const timestamps = events.map((e) => e.timestamp);
  const sessionDuration = Math.max(...timestamps) - Math.min(...timestamps);

  // Calculate words per minute
  const totalCharsTyped = inserts.reduce(
    (sum, e) => sum + (e.content?.length ?? 0),
    0
  );
  const wordsTyped = totalCharsTyped / 5; // Approximate words
  const minutesElapsed = sessionDuration / 60000;
  const wpm = minutesElapsed > 0 ? wordsTyped / minutesElapsed : 0;

  // Calculate paste ratio
  const totalPastedChars = pastes.reduce(
    (sum, e) => sum + (e.content?.length ?? 0),
    0
  );
  const totalContentChars = totalCharsTyped + totalPastedChars;
  const pasteRatio =
    totalContentChars > 0 ? totalPastedChars / totalContentChars : 0;

  return {
    totalEvents: events.length,
    totalKeystrokes: inserts.length,
    totalPastes: pastes.length,
    totalUndos: undos.length,
    totalRedos: redos.length,
    averageKeystrokeIntervalMs: avgInterval,
    keystrokeIntervalVariance: variance,
    largestPasteSize: largestPaste,
    sessionDurationMs: sessionDuration,
    wordsPerMinute: wpm,
    pasteRatio,
  };
}

/**
 * Metrics calculated from edit events.
 */
export interface EditMetrics {
  totalEvents: number;
  totalKeystrokes: number;
  totalPastes: number;
  totalUndos: number;
  totalRedos: number;
  averageKeystrokeIntervalMs: number;
  keystrokeIntervalVariance: number;
  largestPasteSize: number;
  sessionDurationMs: number;
  wordsPerMinute: number;
  /** Ratio of pasted content to total content (0-1) */
  pasteRatio: number;
}
